import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2021 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : qa jobs
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Icon } from 'semantic-ui-react';
// ---Framework imports
import log from 'uix/logging';
import { sum, groupBy, dict, sorted, keys } from 'ldash';
import { prettyBytes } from 'uix/utils';
import { TranslatedTable, Link, HeaderSegment, XMenu } from 'uix/components';
import { DomainLabel } from 'uix/domain/label';
// ---Exports
const ORDER = {
    info: 10,
    warn: 20,
    error: 30
};
const buildIcon = (value)=>({
        error: /*#__PURE__*/ _jsx(Icon, {
            name: "exclamation",
            color: "red"
        }),
        warn: /*#__PURE__*/ _jsx(Icon, {
            name: "exclamation",
            color: "yellow"
        }),
        info: /*#__PURE__*/ _jsx(Icon, {
            name: "info",
            color: "blue"
        }),
        ok: /*#__PURE__*/ _jsx(Icon, {
            name: "check",
            color: "green"
        }),
        skip: /*#__PURE__*/ _jsx(Icon, {
            name: "check",
            color: "grey"
        })
    })[value] || null;
export const OaiOutputValidator = {
    report: ({ events, eventTypes, jobinfo })=>{
        if (!eventTypes.validation_error) {
            return null;
        }
        let last_stats = eventTypes.stats?.slice(-1)[0];
        let errtypes = dict(eventTypes.validation_error?.map((e)=>[
                e.error_type,
                e
            ]));
        let errstats = dict(eventTypes.error_stats?.map((e)=>[
                e.error_type,
                e
            ]));
        let examples = groupBy(eventTypes.error_example, (e)=>e.error_type);
        let err_names = sorted(keys(errtypes), (e)=>-ORDER[errtypes[e].error_level]);
        let data = err_names.map((e)=>({
                ...errtypes[e],
                afflicted: errstats[e]?.fail_ratio || 0,
                num_failed: errstats[e]?.num_failed || 0
            }));
        let columns = [
            {
                Cell: ({ value })=>buildIcon(value),
                accessor: 'error_level',
                width: 25
            },
            {
                id: 'errtype',
                Header: 'Type',
                Cell: ({ value, original })=>{
                    return /*#__PURE__*/ _jsxs("div", {
                        style: {
                            whiteSpace: 'normal'
                        },
                        children: [
                            /*#__PURE__*/ _jsx("b", {
                                children: value
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                children: /*#__PURE__*/ _jsx("i", {
                                    children: original.error_context
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                children: original.error_text
                            })
                        ]
                    });
                },
                accessor: 'error_type'
            },
            {
                id: 'afflicted',
                Header: 'Afflicted',
                accessor: 'afflicted',
                Cell: ({ value, original })=>/*#__PURE__*/ _jsxs("div", {
                        children: [
                            `${value}%`,
                            /*#__PURE__*/ _jsx("br", {}),
                            `${original.num_failed}`
                        ]
                    }),
                width: 70
            },
            {
                id: 'examples',
                Header: 'Examples',
                Cell: ({ original })=>{
                    let md_examples = examples[original.error_type] || [];
                    let links = md_examples.map((node, i)=>/*#__PURE__*/ _jsxs(Link, {
                            id: node.otid,
                            odid: node.odid,
                            classname: "tree",
                            tabname: "bibformat",
                            query: {
                                format: 'zmets'
                            },
                            children: [
                                "[",
                                i,
                                "]",
                                ' '
                            ]
                        }, node.otid));
                    return /*#__PURE__*/ _jsx("div", {
                        style: {
                            whiteSpace: 'normal'
                        },
                        children: links
                    });
                }
            }
        ];
        let header = null;
        if (jobinfo?.kwds) {
            header = /*#__PURE__*/ _jsxs("span", {
                children: [
                    "Prüfung ",
                    /*#__PURE__*/ _jsx(DomainLabel, {
                        dname: jobinfo.kwds['job.domainName']
                    }),
                    ' '
                ]
            });
            if (jobinfo.kwds.otids) {
                header = `Prüfung über ${jobinfo.kwds.otids.length} IDs`;
            }
        }
        let subheader = null;
        let menu = null;
        if (last_stats) {
            let { num_records_processed, total_records } = last_stats;
            subheader = `Verarbeitet: ${num_records_processed}/${total_records}`;
            let item = (name, s, data_path)=>/*#__PURE__*/ _jsxs(XMenu.Item, {
                    children: [
                        buildIcon(name),
                        last_stats[data_path || `${name}_ratio`],
                        '% ',
                        s
                    ]
                });
            menu = /*#__PURE__*/ _jsxs(XMenu, {
                children: [
                    item('error', 'Fehler', 'fail_ratio'),
                    item('warn', 'Warn'),
                    item('info', 'Info'),
                    item('ok', 'Ok'),
                    item('skip', 'Skip')
                ]
            });
        }
        return /*#__PURE__*/ _jsx(HeaderSegment, {
            basic: true,
            header: header,
            subheader: subheader,
            menu: menu,
            children: /*#__PURE__*/ _jsx(TranslatedTable, {
                data: data,
                columns: columns
            })
        });
    }
};
